import { Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { SemanticTileKindNode } from '@/components/diagrams/components/Tile/Tile.utils';
import { useFormContext } from '@/components/react-hook-form';
import { DispositiveProvisionTileIcon } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/DispositiveProvisionTileIcon';
import { pluralize } from '@/utils/formatting/strings';

import {
  DispositiveProvisionsFormV2Shape,
  PrimaryClientDeathEvent,
} from '../../DispositiveProvisionsFormV2.types';
import { getDeathEventBasePath } from '../../DispositiveProvisionsFormV2.utils';

interface DisposingObjectHeaderProps {
  displayName: string;
  node: SemanticTileKindNode;
}

export function DisposingObjectHeader({
  displayName,
  node,
}: DisposingObjectHeaderProps) {
  const { control } = useFormContext<DispositiveProvisionsFormV2Shape>();

  const [firstDeathRecipients, secondDeathRecipients] = useWatch({
    control,
    name: [
      `${getDeathEventBasePath(PrimaryClientDeathEvent.FIRST_DEATH)}.recipients`,
      `${getDeathEventBasePath(PrimaryClientDeathEvent.SECOND_DEATH)}.recipients`,
    ],
  });

  const numberOfRecipients = useMemo(() => {
    return (
      (firstDeathRecipients?.length || 0) + (secondDeathRecipients?.length || 0)
    );
  }, [firstDeathRecipients, secondDeathRecipients]);

  const subtitleText = useMemo(() => {
    if (numberOfRecipients === 0) {
      return 'No dispositions';
    }
    return pluralize({
      count: numberOfRecipients,
      singular: 'disposition',
      includeCount: true,
    });
  }, [numberOfRecipients]);

  return (
    <Stack direction="row" alignItems="center" spacing={2} px={2} mb={2}>
      <DispositiveProvisionTileIcon
        size="large"
        variant="subtle"
        shape="circle"
        node={node}
      />
      <Stack direction="column" spacing={0.5}>
        <Typography variant="title-s">{displayName}</Typography>
        <Typography variant="body-xs">{subtitleText}</Typography>
      </Stack>
    </Stack>
  );
}
